import "./styles.css";
import brandLogoUrl from "./assets/justool_withtxt.webp";
import justOneApiIconUrl from "./assets/justoneapi-icon.png";
import justSerpApiIconUrl from "./assets/justserpapi-icon.ico";

import { localeLabels, localePacks } from "./content";
import {
  createReadyMessage,
  getReferrerOrigin,
  isAllowedPrefillOrigin,
  parsePrefillMessage,
  resolveStartupState
} from "./startup";
import {
  createSecondaryModeInputDrafts,
  createModeInputDrafts,
  switchModeInputDraft
} from "./toolDrafts";
import {
  defaultToolId,
  getModeOutputKind,
  isUrlEncodeModeId,
  toolDefinitions
} from "./transforms";
import {
  analyzeCronExpression,
  detectCronFlavor
} from "./cronUtils";
import {
  convertDateTimeInput,
  convertTimestampInput,
  createCurrentTimestampResult,
  formatDateTimeLocalInput,
  formatDateTimeInTimeZoneInput,
  formatLocalDateTime,
  getTimestampSelectableTimeZones,
  getResolvedTimeZone
} from "./timestampUtils";
import type {
  CronFlavor,
  Locale,
  ToolDefinition,
  ToolId,
  ToolLayoutKind,
  ToolModeId,
  TransformErrorCode,
  TransformStats,
  UrlActionId,
  UrlEncodeModeId,
  WorkerResponse
} from "./types";
import type { PrefillPayload } from "./startup";
import type { TimestampInputUnit } from "./timestampUtils";

const storageKeys = {
  input: "justtool.input",
  mode: "justtool.mode",
  tool: "justtool.tool"
} as const;

const app = document.querySelector<HTMLDivElement>("#app");

if (!app) {
  throw new Error("Application root is missing.");
}

window.localStorage.removeItem(storageKeys.input);

const startupState = resolveStartupState({
  search: window.location.search,
  storedToolId: window.localStorage.getItem(storageKeys.tool),
  storedModeId: window.localStorage.getItem(storageKeys.mode)
});

const worker = new Worker(new URL("./worker.ts", import.meta.url), {
  type: "module"
});

const AUTO_RUN_DELAY_MS = 180;

type StatusKind =
  | "idle"
  | "sampleLoaded"
  | "cleared"
  | "copied"
  | "swapped"
  | "running"
  | "completed"
  | "error";

interface ViewState {
  locale: Locale;
  activeToolId: ToolId;
  activeModeId: ToolModeId;
  input: string;
  secondaryInput: string;
  output: string;
  busy: boolean;
  stats: TransformStats | null;
  statusKind: StatusKind;
  errorCode: TransformErrorCode | null;
}

interface TimestampToolState {
  timestampInput: string;
  timestampUnit: TimestampInputUnit;
  datetimeInput: string;
  datetimeTimeZone: string;
}

interface CronToolState {
  flavor: CronFlavor;
  drafts: Record<CronFlavor, string>;
}

const initialToolId = startupState.toolId;
const initialModeId = startupState.modeId;
const initialUrlEncodeModeId: UrlEncodeModeId = isUrlEncodeModeId(initialModeId)
  ? initialModeId
  : "url-safe";

const state: ViewState = {
  locale: startupState.locale,
  activeToolId: initialToolId,
  activeModeId: initialModeId,
  input: "",
  secondaryInput: "",
  output: "",
  busy: false,
  stats: null,
  statusKind: "idle",
  errorCode: null
};

const timestampToolState: TimestampToolState = {
  timestampInput: createCurrentTimestampResult().seconds,
  timestampUnit: "auto",
  datetimeInput: formatDateTimeLocalInput(new Date()),
  datetimeTimeZone: getResolvedTimeZone()
};

const cronToolState: CronToolState = {
  flavor: "linux",
  drafts: {
    linux: "0 */12 * * *",
    spring: "0 0 */12 * * *",
    quartz: "0 0 18 L * ?"
  }
};

const toolLookup = new Map<ToolId, ToolDefinition>(
  toolDefinitions.map((tool) => [tool.id, tool])
);
const modeInputDrafts = createModeInputDrafts();
const secondaryModeInputDrafts = createSecondaryModeInputDrafts();

let requestSequence = 0;
let autoRunTimer: number | null = null;
let lastUrlEncodeModeId = initialUrlEncodeModeId;
let jsonTreeCacheOutput = "";
let jsonTreeCacheValue: unknown | null = null;
let clearFeedbackTimer: number | null = null;
let copyFeedbackTimer: number | null = null;
let timestampLiveTimer: number | null = null;
const timestampCopyTimers = new WeakMap<HTMLButtonElement, number>();

app.innerHTML = `
  <div class="page-shell">
    <main class="workspace">
      <aside class="sidebar panel">
        <div class="sidebar-head">
          <div class="brand-wrap">
            <button id="brand-home-button" class="brand-home-button" type="button">
              <img
                class="brand-logo"
                src="${brandLogoUrl}"
                alt="justtool"
              />
            </button>
          </div>

          <div class="locale-switch" id="locale-switch">
            <button class="locale-button" data-locale="zh" type="button">中文</button>
            <button class="locale-button" data-locale="en" type="button">EN</button>
          </div>
        </div>

        <div class="tool-list" id="tool-list"></div>
      </aside>

      <section class="main-area">
        <section class="toolbar-panel panel">
          <div class="toolbar-row">
            <div class="toolbar-primary">
              <div class="switch-group">
                <span class="switch-label" id="modes-label"></span>
                <div class="mode-switch" id="mode-switch"></div>
              </div>

              <div class="submode-group" id="submode-group" hidden>
                <span class="submode-label" id="submode-label"></span>
                <div class="mode-switch secondary-mode-switch" id="submode-switch"></div>
              </div>
            </div>

            <nav class="toolbar-links" aria-label="Official sites">
              <a
                id="just-one-api-link"
                class="toolbar-site-link"
                href="https://justoneapi.com/?utm_source=justonetools.com&amp;utm_medium=referral&amp;utm_campaign=product_navigation&amp;utm_content=just_one_api"
                target="_blank"
                rel="noreferrer noopener"
              >
                <span class="toolbar-site-icon" aria-hidden="true">
                  <img
                    class="toolbar-site-icon-image"
                    src="${justOneApiIconUrl}"
                    alt=""
                  />
                </span>
                <span class="toolbar-site-copy">
                  <span class="toolbar-site-title"></span>
                  <span class="toolbar-site-summary"></span>
                </span>
              </a>
              <a
                id="just-serp-api-link"
                class="toolbar-site-link toolbar-site-link--serp"
                href="https://justserpapi.com/?utm_source=justonetools.com&amp;utm_medium=referral&amp;utm_campaign=product_navigation&amp;utm_content=just_serp_api"
                target="_blank"
                rel="noreferrer noopener"
              >
                <span class="toolbar-site-icon" aria-hidden="true">
                  <img
                    class="toolbar-site-icon-image"
                    src="${justSerpApiIconUrl}"
                    alt=""
                  />
                </span>
                <span class="toolbar-site-copy">
                  <span class="toolbar-site-title"></span>
                  <span class="toolbar-site-summary"></span>
                </span>
              </a>
            </nav>

            <div class="toolbar-note">
              <span id="feedback-contact-label" class="toolbar-note-label"></span>
              <a
                class="toolbar-note-link"
                href="mailto:support@justoneapi.com"
              >support@justoneapi.com</a>
            </div>
          </div>
        </section>

        <section class="editor-grid">
          <section class="editor-card panel">
            <div class="editor-surface">
              <div class="editor-card-toolbar">
                <span
                  id="clear-input-feedback"
                  class="editor-action-feedback"
                  hidden
                ></span>
                <button
                  id="clear-input-button"
                  class="ghost-button editor-action-button"
                  type="button"
                ></button>
              </div>

              <textarea
                id="input-editor"
                class="editor"
                spellcheck="false"
              ></textarea>
            </div>
          </section>

          <section class="editor-card panel">
            <div class="editor-surface">
              <div class="editor-card-toolbar">
                <span
                  id="copy-output-feedback"
                  class="editor-action-feedback"
                  hidden
                ></span>
                <button
                  id="copy-output-button"
                  class="ghost-button editor-action-button"
                  type="button"
                ></button>
                <button
                  id="download-image-button"
                  class="ghost-button editor-action-button"
                  type="button"
                  hidden
                ></button>
              </div>

              <textarea
                id="output-editor"
                class="editor output-editor"
                spellcheck="false"
                readonly
              ></textarea>

              <div id="json-tree" class="json-tree" hidden></div>

              <iframe
                id="html-preview"
                class="html-preview"
                sandbox="allow-same-origin"
                hidden
              ></iframe>

              <div id="image-preview" class="image-preview" hidden>
                <img id="image-preview-image" class="image-preview-image" alt="" />
              </div>

              <div id="timestamp-results" class="timestamp-results" hidden></div>
            </div>
          </section>

          <section id="diff-result-card" class="editor-card panel" hidden>
            <div class="editor-surface">
              <div id="text-diff-results" class="text-diff-results" hidden></div>
            </div>
          </section>
        </section>

        <section id="timestamp-tool-panel" class="timestamp-tool panel" hidden>
          <div class="timestamp-tool-layout">
            <section class="timestamp-card timestamp-card--live">
              <div class="timestamp-section-head">
                <div class="timestamp-section-copy">
                  <h2 id="timestamp-live-title" class="timestamp-section-title"></h2>
                  <p
                    id="timestamp-live-summary"
                    class="timestamp-section-summary"
                  ></p>
                </div>
              </div>

              <div class="timestamp-live-grid">
                <div class="timestamp-live-metric">
                  <div class="timestamp-item-head">
                    <span
                      id="timestamp-live-seconds-label"
                      class="timestamp-live-label"
                    ></span>
                    <button
                      id="timestamp-live-seconds-copy"
                      class="timestamp-copy-button"
                      type="button"
                    ></button>
                  </div>
                  <strong
                    id="timestamp-live-seconds-value"
                    class="timestamp-live-value"
                  ></strong>
                </div>

                <div class="timestamp-live-metric">
                  <div class="timestamp-item-head">
                    <span
                      id="timestamp-live-milliseconds-label"
                      class="timestamp-live-label"
                    ></span>
                    <button
                      id="timestamp-live-milliseconds-copy"
                      class="timestamp-copy-button"
                      type="button"
                    ></button>
                  </div>
                  <strong
                    id="timestamp-live-milliseconds-value"
                    class="timestamp-live-value timestamp-live-value--small"
                  ></strong>
                </div>
              </div>

              <div class="timestamp-live-meta">
                <div class="timestamp-live-meta-item">
                  <div class="timestamp-item-head">
                    <span
                      id="timestamp-live-local-label"
                      class="timestamp-live-meta-label"
                    ></span>
                    <button
                      id="timestamp-live-local-copy"
                      class="timestamp-copy-button"
                      type="button"
                    ></button>
                  </div>
                  <strong
                    id="timestamp-live-local-value"
                    class="timestamp-live-meta-value"
                  ></strong>
                </div>

                <div class="timestamp-live-meta-item">
                  <div class="timestamp-item-head">
                    <span
                      id="timestamp-live-utc-label"
                      class="timestamp-live-meta-label"
                    ></span>
                    <button
                      id="timestamp-live-utc-copy"
                      class="timestamp-copy-button"
                      type="button"
                    ></button>
                  </div>
                  <strong
                    id="timestamp-live-utc-value"
                    class="timestamp-live-meta-value"
                  ></strong>
                </div>

                <div class="timestamp-live-meta-item">
                  <div class="timestamp-item-head">
                    <span
                      id="timestamp-live-timezone-label"
                      class="timestamp-live-meta-label"
                    ></span>
                    <button
                      id="timestamp-live-timezone-copy"
                      class="timestamp-copy-button"
                      type="button"
                    ></button>
                  </div>
                  <strong
                    id="timestamp-live-timezone-value"
                    class="timestamp-live-meta-value"
                  ></strong>
                </div>
              </div>
            </section>

            <div class="timestamp-converter-grid">
              <section class="timestamp-card">
                <div class="timestamp-section-head">
                  <div class="timestamp-section-copy">
                    <h2
                      id="timestamp-from-title"
                      class="timestamp-section-title"
                    ></h2>
                    <p
                      id="timestamp-from-summary"
                      class="timestamp-section-summary"
                    ></p>
                  </div>

                  <button
                    id="timestamp-fill-current-button"
                    class="ghost-button timestamp-inline-button"
                    type="button"
                  ></button>
                </div>

                <div class="timestamp-form-row">
                  <input
                    id="timestamp-convert-input"
                    class="timestamp-field"
                    type="text"
                  />

                  <select
                    id="timestamp-unit-select"
                    class="timestamp-select"
                  ></select>
                </div>

                <div
                  id="timestamp-convert-error"
                  class="timestamp-inline-error"
                  hidden
                ></div>

                <div
                  id="timestamp-convert-results"
                  class="timestamp-result-grid"
                ></div>
              </section>

              <section class="timestamp-card">
                <div class="timestamp-section-head">
                  <div class="timestamp-section-copy">
                    <h2 id="datetime-from-title" class="timestamp-section-title"></h2>
                    <p
                      id="datetime-from-summary"
                      class="timestamp-section-summary"
                    ></p>
                  </div>

                  <button
                    id="datetime-fill-now-button"
                    class="ghost-button timestamp-inline-button"
                    type="button"
                  ></button>
                </div>

                <div class="timestamp-form-row timestamp-form-row--datetime">
                  <input
                    id="datetime-convert-input"
                    class="timestamp-field"
                    type="text"
                    spellcheck="false"
                  />

                  <select
                    id="datetime-timezone-select"
                    class="timestamp-select timestamp-timezone-select"
                  ></select>
                </div>

                <div
                  id="datetime-convert-error"
                  class="timestamp-inline-error"
                  hidden
                ></div>

                <div
                  id="datetime-convert-results"
                  class="timestamp-result-grid"
                ></div>
              </section>
            </div>
          </div>
        </section>

        <section id="cron-tool-panel" class="cron-tool panel" hidden>
          <div class="cron-tool-layout">
            <div class="cron-main-column">
              <section class="cron-card cron-card--hero">
                <div class="cron-section-head">
                  <div class="cron-section-copy">
                    <h2 id="cron-tool-title" class="cron-section-title"></h2>
                    <p id="cron-tool-summary" class="cron-section-summary"></p>
                  </div>
                  <div id="cron-current-timezone" class="cron-timezone-badge"></div>
                </div>

                <div class="cron-flavor-row">
                  <span id="cron-type-label" class="cron-row-label"></span>
                  <div id="cron-flavor-switch" class="cron-flavor-switch"></div>
                </div>

                <div class="cron-expression-row">
                  <div class="cron-expression-main">
                    <label
                      id="cron-expression-label"
                      class="cron-row-label"
                      for="cron-expression-input"
                    ></label>
                    <input
                      id="cron-expression-input"
                      class="cron-expression-input"
                      type="text"
                      spellcheck="false"
                    />
                  </div>
                </div>

                <div class="cron-hint-grid">
                  <div class="cron-hint-card">
                    <span id="cron-field-guide-label" class="cron-hint-label"></span>
                    <code id="cron-field-guide-value" class="cron-hint-code"></code>
                  </div>

                  <div class="cron-hint-card">
                    <span id="cron-example-label" class="cron-hint-label"></span>
                    <code id="cron-example-value" class="cron-hint-code"></code>
                  </div>
                </div>

                <p id="cron-flavor-hint" class="cron-flavor-hint"></p>

                <div id="cron-tool-error" class="cron-tool-error" hidden></div>
              </section>

              <section class="cron-card">
                <div class="cron-card-head">
                  <h3 id="cron-description-title" class="cron-card-title"></h3>
                </div>

                <div id="cron-description-body" class="cron-description-body"></div>
              </section>
            </div>

            <section class="cron-card cron-card--runs">
              <div class="cron-card-head">
                <h3 id="cron-next-runs-title" class="cron-card-title"></h3>
              </div>

              <div id="cron-next-runs-list" class="cron-next-runs-list"></div>
            </section>
          </div>
        </section>
      </section>
    </main>
  </div>
`;

const toolList = getElement<HTMLDivElement>("#tool-list");
const brandHomeButton = getElement<HTMLButtonElement>("#brand-home-button");
const toolbarPanel = getElement<HTMLElement>(".toolbar-panel");
const modeSwitch = getElement<HTMLDivElement>("#mode-switch");
const submodeGroup = getElement<HTMLDivElement>("#submode-group");
const submodeLabel = getElement<HTMLSpanElement>("#submode-label");
const submodeSwitch = getElement<HTMLDivElement>("#submode-switch");
const localeSwitch = getElement<HTMLDivElement>("#locale-switch");
const modesLabel = getElement<HTMLSpanElement>("#modes-label");
const feedbackContactLabel = getElement<HTMLSpanElement>("#feedback-contact-label");
const justOneApiLink = getElement<HTMLAnchorElement>("#just-one-api-link");
const justSerpApiLink = getElement<HTMLAnchorElement>("#just-serp-api-link");
const editorGrid = getElement<HTMLElement>(".editor-grid");
const inputEditor = getElement<HTMLTextAreaElement>("#input-editor");
const outputEditor = getElement<HTMLTextAreaElement>("#output-editor");
const clearInputButton = getElement<HTMLButtonElement>("#clear-input-button");
const copyOutputButton = getElement<HTMLButtonElement>("#copy-output-button");
const downloadImageButton = getElement<HTMLButtonElement>("#download-image-button");
const clearInputFeedback = getElement<HTMLSpanElement>("#clear-input-feedback");
const copyOutputFeedback = getElement<HTMLSpanElement>("#copy-output-feedback");
const jsonTree = getElement<HTMLDivElement>("#json-tree");
const htmlPreview = getElement<HTMLIFrameElement>("#html-preview");
const imagePreview = getElement<HTMLDivElement>("#image-preview");
const imagePreviewImage = getElement<HTMLImageElement>("#image-preview-image");
const timestampResults = getElement<HTMLDivElement>("#timestamp-results");
const diffResultCard = getElement<HTMLElement>("#diff-result-card");
const textDiffResults = getElement<HTMLDivElement>("#text-diff-results");
const timestampToolPanel = getElement<HTMLElement>("#timestamp-tool-panel");
const timestampLiveTitle = getElement<HTMLHeadingElement>("#timestamp-live-title");
const timestampLiveSummary = getElement<HTMLParagraphElement>("#timestamp-live-summary");
const timestampLiveSecondsLabel = getElement<HTMLSpanElement>(
  "#timestamp-live-seconds-label"
);
const timestampLiveSecondsValue = getElement<HTMLSpanElement>(
  "#timestamp-live-seconds-value"
);
const timestampLiveSecondsCopy = getElement<HTMLButtonElement>(
  "#timestamp-live-seconds-copy"
);
const timestampLiveMillisecondsLabel = getElement<HTMLSpanElement>(
  "#timestamp-live-milliseconds-label"
);
const timestampLiveMillisecondsValue = getElement<HTMLSpanElement>(
  "#timestamp-live-milliseconds-value"
);
const timestampLiveMillisecondsCopy = getElement<HTMLButtonElement>(
  "#timestamp-live-milliseconds-copy"
);
const timestampLiveLocalLabel = getElement<HTMLSpanElement>("#timestamp-live-local-label");
const timestampLiveLocalValue = getElement<HTMLSpanElement>("#timestamp-live-local-value");
const timestampLiveLocalCopy = getElement<HTMLButtonElement>("#timestamp-live-local-copy");
const timestampLiveUtcLabel = getElement<HTMLSpanElement>("#timestamp-live-utc-label");
const timestampLiveUtcValue = getElement<HTMLSpanElement>("#timestamp-live-utc-value");
const timestampLiveUtcCopy = getElement<HTMLButtonElement>("#timestamp-live-utc-copy");
const timestampLiveTimezoneLabel = getElement<HTMLSpanElement>(
  "#timestamp-live-timezone-label"
);
const timestampLiveTimezoneValue = getElement<HTMLSpanElement>(
  "#timestamp-live-timezone-value"
);
const timestampLiveTimezoneCopy = getElement<HTMLButtonElement>(
  "#timestamp-live-timezone-copy"
);
const timestampFromTitle = getElement<HTMLHeadingElement>("#timestamp-from-title");
const timestampFromSummary = getElement<HTMLParagraphElement>("#timestamp-from-summary");
const timestampFillCurrentButton = getElement<HTMLButtonElement>(
  "#timestamp-fill-current-button"
);
const timestampConvertInput = getElement<HTMLInputElement>("#timestamp-convert-input");
const timestampUnitSelect = getElement<HTMLSelectElement>("#timestamp-unit-select");
const timestampConvertError = getElement<HTMLDivElement>("#timestamp-convert-error");
const timestampConvertResults = getElement<HTMLDivElement>("#timestamp-convert-results");
const datetimeFromTitle = getElement<HTMLHeadingElement>("#datetime-from-title");
const datetimeFromSummary = getElement<HTMLParagraphElement>("#datetime-from-summary");
const datetimeFillNowButton = getElement<HTMLButtonElement>("#datetime-fill-now-button");
const datetimeConvertInput = getElement<HTMLInputElement>("#datetime-convert-input");
const datetimeTimezoneSelect = getElement<HTMLSelectElement>("#datetime-timezone-select");
const datetimeConvertError = getElement<HTMLDivElement>("#datetime-convert-error");
const datetimeConvertResults = getElement<HTMLDivElement>("#datetime-convert-results");
const cronToolPanel = getElement<HTMLElement>("#cron-tool-panel");
const cronToolTitle = getElement<HTMLHeadingElement>("#cron-tool-title");
const cronToolSummary = getElement<HTMLParagraphElement>("#cron-tool-summary");
const cronCurrentTimezone = getElement<HTMLDivElement>("#cron-current-timezone");
const cronTypeLabel = getElement<HTMLSpanElement>("#cron-type-label");
const cronFlavorSwitch = getElement<HTMLDivElement>("#cron-flavor-switch");
const cronExpressionLabel = getElement<HTMLLabelElement>("#cron-expression-label");
const cronExpressionInput = getElement<HTMLInputElement>("#cron-expression-input");
const cronFieldGuideLabel = getElement<HTMLSpanElement>("#cron-field-guide-label");
const cronFieldGuideValue = getElement<HTMLElement>("#cron-field-guide-value");
const cronExampleLabel = getElement<HTMLSpanElement>("#cron-example-label");
const cronExampleValue = getElement<HTMLElement>("#cron-example-value");
const cronFlavorHint = getElement<HTMLParagraphElement>("#cron-flavor-hint");
const cronToolError = getElement<HTMLDivElement>("#cron-tool-error");
const cronDescriptionTitle = getElement<HTMLHeadingElement>("#cron-description-title");
const cronDescriptionBody = getElement<HTMLDivElement>("#cron-description-body");
const cronNextRunsTitle = getElement<HTMLHeadingElement>("#cron-next-runs-title");
const cronNextRunsList = getElement<HTMLDivElement>("#cron-next-runs-list");

inputEditor.value = state.input;
render();
syncStartupQueryParams();

brandHomeButton.addEventListener("click", () => {
  resetToHome();
});

clearInputButton.addEventListener("click", () => {
  clearAutoRunTimer();
  invalidatePendingResponse();
  state.input = "";
  state.secondaryInput = "";
  state.output = "";
  state.busy = false;
  state.stats = null;
  state.errorCode = null;
  state.statusKind = "idle";
  modeInputDrafts[state.activeModeId] = "";
  secondaryModeInputDrafts[state.activeModeId] = "";
  inputEditor.value = "";
  outputEditor.value = "";
  render();
  showActionFeedback("clear");
  inputEditor.focus();
});

copyOutputButton.addEventListener("click", () => {
  if (state.output === "") {
    showActionFeedback("copy-empty");
    return;
  }

  const copyTask = navigator.clipboard?.writeText(state.output);

  if (copyTask) {
    void copyTask
      .then(() => {
        showActionFeedback("copy");
      })
      .catch(() => {
        showActionFeedback("copy-error");
      });
    return;
  }

  showActionFeedback("copy-error");
});

downloadImageButton.addEventListener("click", () => {
  if (!shouldShowImageDownload() || state.output === "") {
    return;
  }

  downloadBase64Image(state.output);
});

localeSwitch.addEventListener("click", (event) => {
  const target = event.target as HTMLElement | null;
  const button = target?.closest<HTMLButtonElement>("[data-locale]");

  if (!button) {
    return;
  }

  const nextLocale = button.dataset.locale as Locale;

  if (nextLocale === state.locale) {
    return;
  }

  state.locale = nextLocale;
  syncStartupQueryParams();
  render();
});

toolList.addEventListener("click", (event) => {
  const target = event.target as HTMLElement | null;
  const button = target?.closest<HTMLButtonElement>("[data-tool-id]");

  if (!button) {
    return;
  }

  const nextToolId = button.dataset.toolId as ToolId;

  if (!toolLookup.has(nextToolId) || nextToolId === state.activeToolId) {
    return;
  }

  const nextTool = getTool(nextToolId);
  invalidatePendingResponse();
  state.input = switchModeInputDraft(
    modeInputDrafts,
    state.activeModeId,
    state.input,
    nextTool.defaultModeId
  );
  state.secondaryInput = switchModeInputDraft(
    secondaryModeInputDrafts,
    state.activeModeId,
    state.secondaryInput,
    nextTool.defaultModeId
  );
  state.activeToolId = nextToolId;
  state.activeModeId = nextTool.defaultModeId;

  if (isUrlEncodeModeId(state.activeModeId)) {
    lastUrlEncodeModeId = state.activeModeId;
  }

  state.output = "";
  state.stats = null;
  state.errorCode = null;
  state.statusKind = "idle";
  inputEditor.value = state.input;
  outputEditor.value = state.secondaryInput;
  window.localStorage.setItem(storageKeys.tool, nextToolId);
  window.localStorage.setItem(storageKeys.mode, state.activeModeId);
  syncStartupQueryParams();
  render();
  scheduleAutoRun(0);
});

toolbarPanel.addEventListener("click", (event) => {
  const target = event.target as HTMLElement | null;
  const urlActionButton =
    target?.closest<HTMLButtonElement>("[data-url-action]");

  if (urlActionButton) {
    const nextAction = urlActionButton.dataset.urlAction as UrlActionId;
    handleUrlActionChange(nextAction);
    return;
  }

  const button = target?.closest<HTMLButtonElement>("[data-mode-id]");

  if (!button) {
    return;
  }

  const nextModeId = button.dataset.modeId as ToolModeId;
  setActiveMode(nextModeId);
});

inputEditor.addEventListener("input", () => {
  invalidatePendingResponse();
  state.input = inputEditor.value;
  modeInputDrafts[state.activeModeId] = state.input;
  state.output = "";
  state.stats = null;
  state.errorCode = null;
  state.statusKind = "idle";
  outputEditor.value = "";
  scheduleAutoRun();
});

outputEditor.addEventListener("input", () => {
  if (getActiveToolLayoutKind() !== "diff") {
    return;
  }

  invalidatePendingResponse();
  state.secondaryInput = outputEditor.value;
  secondaryModeInputDrafts[state.activeModeId] = state.secondaryInput;
  state.output = "";
  state.stats = null;
  state.errorCode = null;
  state.statusKind = "idle";
  scheduleAutoRun();
});

timestampConvertInput.addEventListener("input", () => {
  timestampToolState.timestampInput = timestampConvertInput.value;
  renderTimestampTool();
});

timestampUnitSelect.addEventListener("change", () => {
  timestampToolState.timestampUnit = timestampUnitSelect
    .value as TimestampInputUnit;
  renderTimestampTool();
});

datetimeConvertInput.addEventListener("input", () => {
  timestampToolState.datetimeInput = datetimeConvertInput.value;
  renderTimestampTool();
});

datetimeTimezoneSelect.addEventListener("change", () => {
  timestampToolState.datetimeTimeZone = datetimeTimezoneSelect.value;
  renderTimestampTool();
});

timestampFillCurrentButton.addEventListener("click", () => {
  const current = createCurrentTimestampResult();

  timestampToolState.timestampInput =
    timestampToolState.timestampUnit === "milliseconds"
      ? current.milliseconds
      : current.seconds;
  renderTimestampTool();
});

datetimeFillNowButton.addEventListener("click", () => {
  timestampToolState.datetimeInput = formatDateTimeInTimeZoneInput(
    new Date(),
    timestampToolState.datetimeTimeZone
  );
  renderTimestampTool();
});

cronExpressionInput.addEventListener("input", () => {
  cronToolState.drafts[cronToolState.flavor] = cronExpressionInput.value;
  renderCronTool();
});

timestampToolPanel.addEventListener("click", (event) => {
  const target = event.target as HTMLElement | null;
  const button = target?.closest<HTMLButtonElement>("[data-copy-text]");

  if (!button) {
    return;
  }

  const text = button.dataset.copyText ?? "";

  if (text.trim() === "") {
    return;
  }

  const copyTask = navigator.clipboard?.writeText(text);

  if (!copyTask) {
    flashTimestampCopyButton(button, false);
    return;
  }

  void copyTask
    .then(() => {
      flashTimestampCopyButton(button, true);
    })
    .catch(() => {
      flashTimestampCopyButton(button, false);
    });
});

cronToolPanel.addEventListener("click", (event) => {
  const target = event.target as HTMLElement | null;
  const flavorButton = target?.closest<HTMLButtonElement>("[data-cron-flavor]");

  if (flavorButton) {
    cronToolState.flavor = flavorButton.dataset.cronFlavor as CronFlavor;
    renderCronTool();
    return;
  }

  const copyButton = target?.closest<HTMLButtonElement>("[data-copy-text]");

  if (!copyButton) {
    return;
  }

  const text = copyButton.dataset.copyText ?? "";

  if (text.trim() === "") {
    return;
  }

  const copyTask = navigator.clipboard?.writeText(text);

  if (!copyTask) {
    flashTimestampCopyButton(copyButton, false);
    return;
  }

  void copyTask
    .then(() => {
      flashTimestampCopyButton(copyButton, true);
    })
    .catch(() => {
      flashTimestampCopyButton(copyButton, false);
    });
});

window.addEventListener("keydown", (event) => {
  if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
    event.preventDefault();
    runActiveMode();
  }
});

window.addEventListener("message", (event: MessageEvent<unknown>) => {
  const message = parsePrefillMessage(event.data);

  if (!message) {
    return;
  }

  if (window.opener && event.source !== window.opener) {
    return;
  }

  if (!isAllowedPrefillOrigin(event.origin, window.location.origin)) {
    return;
  }

  applyPrefill(message.payload);
});

announceReadyToOpener();

worker.addEventListener("message", (event: MessageEvent<WorkerResponse>) => {
  const response = event.data;

  if (response.requestId !== requestSequence) {
    return;
  }

  state.busy = false;
  state.stats = {
    durationMs: response.durationMs,
    inputBytes: response.inputBytes,
    outputBytes: response.outputBytes
  };

  if (response.ok) {
    state.output = response.output;
    state.errorCode = null;
    state.statusKind = "completed";
  } else {
    state.output = "";
    state.errorCode = response.errorCode;
    state.statusKind = "error";
  }

  render();
});

function runActiveMode(): void {
  if (
    getActiveToolLayoutKind() === "timestamp" ||
    getActiveToolLayoutKind() === "cron"
  ) {
    render();
    return;
  }

  clearAutoRunTimer();
  const workerInput = getWorkerInput();

  if (workerInput.trim() === "") {
    state.busy = false;
    state.output = "";
    state.stats = null;
    state.errorCode = null;
    state.statusKind = "idle";
    if (getActiveToolLayoutKind() !== "diff") {
      outputEditor.value = "";
    }
    render();
    return;
  }

  state.busy = true;
  state.errorCode = null;
  state.statusKind = "running";
  render();

  requestSequence += 1;

  worker.postMessage({
    requestId: requestSequence,
    modeId: state.activeModeId,
    input: workerInput
  });
}

function applyPrefill(payload: PrefillPayload): void {
  clearAutoRunTimer();
  invalidatePendingResponse();

  state.locale = payload.lang;
  state.activeToolId = payload.tool;
  state.activeModeId = payload.mode;
  state.input = payload.input;
  state.secondaryInput = secondaryModeInputDrafts[payload.mode];
  state.output = "";
  state.busy = false;
  state.stats = null;
  state.statusKind = "idle";
  state.errorCode = null;

  if (isUrlEncodeModeId(payload.mode)) {
    lastUrlEncodeModeId = payload.mode;
  }

  if (payload.tool === "cron") {
    const flavor = detectCronFlavor(payload.input);
    cronToolState.flavor = flavor;
    cronToolState.drafts[flavor] = payload.input;
  }

  modeInputDrafts[payload.mode] = payload.input;
  inputEditor.value = payload.input;
  outputEditor.value = state.secondaryInput;

  window.localStorage.setItem(storageKeys.tool, payload.tool);
  window.localStorage.setItem(storageKeys.mode, payload.mode);

  syncStartupQueryParams();
  render();
  runActiveMode();
}

function announceReadyToOpener(): void {
  if (!window.opener || window.opener.closed) {
    return;
  }

  const targetOrigin = getReferrerOrigin(document.referrer) ?? "*";

  try {
    window.opener.postMessage(createReadyMessage(), targetOrigin);
  } catch {
    window.opener.postMessage(createReadyMessage(), "*");
  }
}

function render(): void {
  const pack = localePacks[state.locale];
  document.documentElement.lang = state.locale === "en" ? "en" : "zh-CN";
  document.title = getPageTitle();
  const layoutKind = getActiveToolLayoutKind();
  const isTimestampLayout = layoutKind === "timestamp";
  const isCronLayout = layoutKind === "cron";
  const outputKind = getModeOutputKind(state.activeModeId);
  const isPreviewMode =
    outputKind === "html-preview" || outputKind === "image-preview";
  const shouldHideCopyButton =
    isPreviewMode ||
    isTimestampLayout ||
    isCronLayout ||
    outputKind === "timestamp-result" ||
    outputKind === "text-diff-result" ||
    layoutKind === "diff";
  const showImageDownload = shouldShowImageDownload();

  modesLabel.textContent = pack.modesLabel;
  submodeLabel.textContent = pack.urlEncodeModesLabel;
  feedbackContactLabel.textContent = pack.feedbackContactLabel;
  renderOfficialLink(justOneApiLink, pack.officialLinks.justOneApi);
  renderOfficialLink(justSerpApiLink, pack.officialLinks.justSerpApi);
  clearInputButton.textContent = pack.clearLabel;
  copyOutputButton.textContent = pack.copyLabel;
  downloadImageButton.textContent = state.locale === "zh" ? "下载" : "Download";
  clearInputButton.title = pack.clearLabel;
  copyOutputButton.title = pack.copyLabel;
  downloadImageButton.title = downloadImageButton.textContent;
  copyOutputButton.hidden = shouldHideCopyButton;
  downloadImageButton.hidden = !showImageDownload;

  if (shouldHideCopyButton || copyOutputFeedback.textContent?.trim() === "") {
    copyOutputFeedback.hidden = true;
  }

  inputEditor.placeholder = getInputPlaceholder();
  outputEditor.readOnly = layoutKind !== "diff";
  outputEditor.placeholder = getSecondaryInputPlaceholder();
  editorGrid.classList.toggle("editor-grid--json", outputKind === "json");
  editorGrid.classList.toggle(
    "editor-grid--html-preview",
    outputKind === "html-preview"
  );
  editorGrid.classList.toggle("editor-grid--stack", layoutKind === "stack");
  editorGrid.classList.toggle("editor-grid--diff", layoutKind === "diff");
  editorGrid.hidden = isTimestampLayout || isCronLayout;
  diffResultCard.hidden = layoutKind !== "diff";
  timestampToolPanel.hidden = !isTimestampLayout;
  cronToolPanel.hidden = !isCronLayout;

  renderToolList();
  renderModeSwitch();
  renderLocaleSwitch();
  syncTimestampLiveTimer();

  if (isTimestampLayout) {
    renderTimestampTool();
    return;
  }

  if (isCronLayout) {
    renderCronTool();
    return;
  }

  renderOutputPanel();
}

function renderOfficialLink(
  link: HTMLAnchorElement,
  copy: { title: string; summary: string }
): void {
  const title = link.querySelector<HTMLSpanElement>(".toolbar-site-title");
  const summary = link.querySelector<HTMLSpanElement>(".toolbar-site-summary");

  if (!title || !summary) {
    return;
  }

  title.textContent = copy.title;
  summary.textContent = copy.summary;
  link.title = copy.title;
  link.setAttribute("aria-label", copy.title);
}

function renderToolList(): void {
  const pack = localePacks[state.locale];

  toolList.innerHTML = toolDefinitions
    .map((tool) => {
      const copy = pack.tools[tool.id];

      return `
        <button
          class="tool-item ${tool.id === state.activeToolId ? "is-active" : ""}"
          data-tool-id="${tool.id}"
          type="button"
        >
          <strong class="tool-item-label">${copy.title}</strong>
        </button>
      `;
    })
    .join("");
}

function renderModeSwitch(): void {
  const pack = localePacks[state.locale];
  const tool = getTool(state.activeToolId);

  if (tool.id !== "url-encode") {
    modeSwitch.innerHTML = renderModeButtons(tool.modeIds);
    submodeGroup.hidden = true;
    submodeSwitch.innerHTML = "";
    return;
  }

  const urlActionId = getUrlActionId(state.activeModeId);

  modeSwitch.innerHTML = (["encode", "decode"] as const)
    .map(
      (actionId) => `
        <button
          class="mode-button ${actionId === urlActionId ? "is-active" : ""}"
          data-url-action="${actionId}"
          type="button"
        >
          <span>${pack.urlActions[actionId]}</span>
        </button>
      `
    )
    .join("");

  if (urlActionId === "decode") {
    submodeGroup.hidden = true;
    submodeSwitch.innerHTML = "";
    return;
  }

  submodeGroup.hidden = false;
  submodeSwitch.innerHTML = renderModeButtons(
    ["url-safe", "url-full"],
    "mode-button--submode"
  );
}

function renderLocaleSwitch(): void {
  for (const button of localeSwitch.querySelectorAll<HTMLButtonElement>(
    ".locale-button"
  )) {
    const locale = button.dataset.locale as Locale;
    button.textContent = localeLabels[locale];
    button.classList.toggle("is-active", locale === state.locale);
  }
}

function getPageTitle(): string {
  const activeToolTitle = localePacks[state.locale].tools[state.activeToolId].title;
  return `${activeToolTitle} - Just One Tools`;
}

function getOutputPlaceholder(): string {
  const pack = localePacks[state.locale];

  if (state.statusKind === "error") {
    return pack.errorMessages[state.errorCode ?? "UNKNOWN"];
  }

  if (state.input.trim() === "") {
    return pack.outputPlaceholder;
  }

  if (state.busy || state.statusKind === "running") {
    return pack.statusRunning(pack.modes[state.activeModeId].actionLabel);
  }

  return pack.outputPlaceholder;
}

function renderOutputPanel(): void {
  outputEditor.placeholder =
    getActiveToolLayoutKind() === "diff"
      ? getSecondaryInputPlaceholder()
      : getOutputPlaceholder();
  outputEditor.value =
    getActiveToolLayoutKind() === "diff" ? state.secondaryInput : state.output;
  const outputKind = getModeOutputKind(state.activeModeId);

  if (outputKind === "json") {
    renderJsonOutput();
    return;
  }

  if (outputKind === "html-preview") {
    renderHtmlPreviewOutput();
    return;
  }

  if (outputKind === "image-preview") {
    renderImagePreviewOutput();
    return;
  }

  if (outputKind === "timestamp-result") {
    renderTimestampOutput();
    return;
  }

  if (outputKind === "text-diff-result") {
    renderTextDiffOutput();
    return;
  }

  showTextOutput();
}

function writeHtmlPreview(source: string): void {
  htmlPreview.srcdoc = source;

  const previewDocument = htmlPreview.contentDocument;

  if (!previewDocument) {
    return;
  }

  previewDocument.open();
  previewDocument.write(source);
  previewDocument.close();
}

function clearHtmlPreview(): void {
  htmlPreview.srcdoc = "";

  const previewDocument = htmlPreview.contentDocument;

  if (!previewDocument) {
    return;
  }

  previewDocument.open();
  previewDocument.write("");
  previewDocument.close();
}

function clearImagePreview(): void {
  imagePreviewImage.removeAttribute("src");
  imagePreviewImage.alt = "";
}

function downloadBase64Image(dataUrl: string): void {
  const parsed = parseBase64DataUrl(dataUrl);

  if (!parsed) {
    return;
  }

  const bytes = decodeBase64BytesFromDataUrl(parsed.base64);
  const arrayBuffer = new ArrayBuffer(bytes.byteLength);
  const blobBytes = new Uint8Array(arrayBuffer);
  blobBytes.set(bytes);
  const blob = new Blob([arrayBuffer], { type: parsed.mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = objectUrl;
  link.download = `justonetools-base64-image.${getImageFileExtension(parsed.mimeType)}`;
  link.click();

  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, 0);
}

function shouldShowImageDownload(): boolean {
  return (
    getModeOutputKind(state.activeModeId) === "image-preview" &&
    state.statusKind === "completed" &&
    state.output.trim() !== ""
  );
}

function parseBase64DataUrl(
  dataUrl: string
): { base64: string; mimeType: string } | null {
  const match = dataUrl.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,([\s\S]+)$/i);

  if (!match) {
    return null;
  }

  return {
    mimeType: match[1].toLowerCase(),
    base64: match[2]
  };
}

function decodeBase64BytesFromDataUrl(base64: string): Uint8Array {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function getImageFileExtension(mimeType: string): string {
  switch (mimeType) {
    case "image/png":
      return "png";
    case "image/jpeg":
      return "jpg";
    case "image/gif":
      return "gif";
    case "image/webp":
      return "webp";
    case "image/bmp":
      return "bmp";
    case "image/svg+xml":
      return "svg";
    default:
      return "bin";
  }
}

function getInputPlaceholder(): string {
  const pack = localePacks[state.locale];
  return (
    pack.modeInputPlaceholders[state.activeModeId] ??
    pack.toolInputPlaceholders[state.activeToolId]
  );
}

function getSecondaryInputPlaceholder(): string {
  if (getActiveToolLayoutKind() !== "diff") {
    return getOutputPlaceholder();
  }

  return (
    localePacks[state.locale].secondaryInputPlaceholders[state.activeModeId] ?? ""
  );
}

function renderJsonOutput(): void {
  if (!hasRenderableOutput()) {
    showTextOutput();
    return;
  }

  const parsed = getJsonTreeValue();

  clearHtmlPreview();
  clearImagePreview();

  if (parsed === null) {
    showTextOutput();
    return;
  }

  outputEditor.hidden = true;
  htmlPreview.hidden = true;
  imagePreview.hidden = true;
  timestampResults.hidden = true;
  timestampResults.replaceChildren();
  textDiffResults.hidden = true;
  textDiffResults.replaceChildren();
  jsonTree.hidden = false;
  jsonTree.replaceChildren(createJsonTreeRoot(parsed));
}

function renderHtmlPreviewOutput(): void {
  if (!hasRenderableOutput()) {
    showTextOutput();
    return;
  }

  outputEditor.hidden = true;
  jsonTree.hidden = true;
  jsonTree.replaceChildren();
  imagePreview.hidden = true;
  clearImagePreview();
  timestampResults.hidden = true;
  timestampResults.replaceChildren();
  textDiffResults.hidden = true;
  textDiffResults.replaceChildren();
  htmlPreview.hidden = false;
  writeHtmlPreview(state.output);
}

function renderImagePreviewOutput(): void {
  if (!hasRenderableOutput()) {
    showTextOutput();
    return;
  }

  outputEditor.hidden = true;
  jsonTree.hidden = true;
  jsonTree.replaceChildren();
  htmlPreview.hidden = true;
  clearHtmlPreview();
  timestampResults.hidden = true;
  timestampResults.replaceChildren();
  textDiffResults.hidden = true;
  textDiffResults.replaceChildren();
  imagePreview.hidden = false;
  imagePreviewImage.src = state.output;
  imagePreviewImage.alt =
    state.locale === "zh" ? "Base64 图片预览" : "Base64 image preview";
}

function showTextOutput(): void {
  outputEditor.hidden = false;
  jsonTree.hidden = true;
  jsonTree.replaceChildren();
  htmlPreview.hidden = true;
  clearHtmlPreview();
  imagePreview.hidden = true;
  clearImagePreview();
  timestampResults.hidden = true;
  timestampResults.replaceChildren();
  textDiffResults.hidden = true;
  textDiffResults.replaceChildren();
}

function hasRenderableOutput(): boolean {
  return state.statusKind === "completed" && state.output.trim() !== "";
}

function renderTimestampOutput(): void {
  if (!hasRenderableOutput()) {
    showTextOutput();
    outputEditor.hidden = true;
    return;
  }

  const result = parseJsonOutput<{
    detectedType: "seconds" | "milliseconds" | "datetime";
    seconds: string;
    milliseconds: string;
    isoUtc: string;
  }>();

  if (!result) {
    showTextOutput();
    return;
  }

  const pack = localePacks[state.locale];
  const localTime = new Date(result.isoUtc).toLocaleString(
    state.locale === "zh" ? "zh-CN" : "en-US",
    {
      hour12: false
    }
  );

  timestampResults.replaceChildren(
    ...[
      createTimestampItem(
        pack.timestampResult.detectedType,
        pack.timestampResult.detectedTypes[result.detectedType]
      ),
      createTimestampItem(pack.timestampResult.seconds, result.seconds),
      createTimestampItem(pack.timestampResult.milliseconds, result.milliseconds),
      createTimestampItem(pack.timestampResult.isoUtc, result.isoUtc),
      createTimestampItem(pack.timestampResult.localTime, localTime)
    ]
  );
  outputEditor.hidden = true;
  jsonTree.hidden = true;
  jsonTree.replaceChildren();
  htmlPreview.hidden = true;
  clearHtmlPreview();
  imagePreview.hidden = true;
  clearImagePreview();
  textDiffResults.hidden = true;
  textDiffResults.replaceChildren();
  timestampResults.hidden = false;
}

function renderTextDiffOutput(): void {
  if (!hasRenderableOutput()) {
    textDiffResults.hidden = false;
    textDiffResults.replaceChildren(createTextDiffEmptyState());
    outputEditor.hidden = false;
    jsonTree.hidden = true;
    jsonTree.replaceChildren();
    htmlPreview.hidden = true;
    clearHtmlPreview();
    imagePreview.hidden = true;
    clearImagePreview();
    timestampResults.hidden = true;
    timestampResults.replaceChildren();
    return;
  }

  const result = parseJsonOutput<{
    summary: { added: number; removed: number; unchanged: number };
    rows: Array<{
      type: "unchanged" | "added" | "removed";
      leftLine: number | null;
      rightLine: number | null;
      text: string;
    }>;
  }>();

  if (!result) {
    showTextOutput();
    return;
  }

  const pack = localePacks[state.locale];
  const container = document.createDocumentFragment();
  const summary = document.createElement("div");
  summary.className = "text-diff-summary";
  summary.textContent = pack.textDiff.summary(result.summary);
  container.appendChild(summary);

  if (result.rows.every((row) => row.type === "unchanged")) {
    const empty = document.createElement("div");
    empty.className = "text-diff-empty";
    empty.textContent = pack.textDiff.empty;
    container.appendChild(empty);
  } else {
    const list = document.createElement("div");
    list.className = "text-diff-list";

    for (const row of result.rows) {
      list.appendChild(createTextDiffRow(row));
    }

    container.appendChild(list);
  }

  textDiffResults.replaceChildren(container);
  textDiffResults.hidden = false;
  outputEditor.hidden = false;
  jsonTree.hidden = true;
  jsonTree.replaceChildren();
  htmlPreview.hidden = true;
  clearHtmlPreview();
  imagePreview.hidden = true;
  clearImagePreview();
  timestampResults.hidden = true;
  timestampResults.replaceChildren();
}

function renderTimestampTool(): void {
  const pack = localePacks[state.locale];
  const timezone = getResolvedTimeZone();

  timestampLiveTitle.textContent = pack.timestampTool.liveTitle;
  timestampLiveSummary.textContent = pack.timestampTool.liveSummary;
  timestampLiveSecondsLabel.textContent = pack.timestampTool.secondsLabel;
  timestampLiveMillisecondsLabel.textContent = pack.timestampTool.millisecondsLabel;
  timestampLiveLocalLabel.textContent = pack.timestampTool.localTimeLabel;
  timestampLiveUtcLabel.textContent = pack.timestampTool.utcTimeLabel;
  timestampLiveTimezoneLabel.textContent = pack.timestampTool.timezoneLabel;
  timestampFromTitle.textContent = pack.timestampTool.fromTimestampTitle;
  timestampFromSummary.textContent = pack.timestampTool.fromTimestampSummary;
  timestampFillCurrentButton.textContent = pack.timestampTool.fillCurrentLabel;
  datetimeFromTitle.textContent = pack.timestampTool.toTimestampTitle;
  datetimeFromSummary.textContent = pack.timestampTool.toTimestampSummary;
  datetimeFillNowButton.textContent = pack.timestampTool.fillNowLabel;
  timestampConvertInput.value = timestampToolState.timestampInput;
  timestampConvertInput.placeholder =
    pack.modeInputPlaceholders["timestamp-convert"] ??
    pack.toolInputPlaceholders.timestamp;
  datetimeConvertInput.value = timestampToolState.datetimeInput;
  datetimeConvertInput.placeholder = pack.timestampTool.datetimePlaceholder;
  syncStaticTimestampCopyButtons();

  renderTimestampUnitOptions();
  renderDateTimeTimeZoneOptions();
  renderCurrentTimestampCard(timezone);
  renderTimestampToDateSection(pack);
  renderDateTimeToTimestampSection(pack);
}

function renderTimestampUnitOptions(): void {
  const pack = localePacks[state.locale];
  const options: Array<{ value: TimestampInputUnit; label: string }> = [
    { value: "auto", label: pack.timestampTool.unitAuto },
    { value: "seconds", label: pack.timestampTool.unitSeconds },
    { value: "milliseconds", label: pack.timestampTool.unitMilliseconds }
  ];

  timestampUnitSelect.innerHTML = options
    .map(
      (option) =>
        `<option value="${option.value}">${option.label}</option>`
    )
    .join("");
  timestampUnitSelect.value = timestampToolState.timestampUnit;
}

function renderDateTimeTimeZoneOptions(): void {
  const timeZones = getTimestampSelectableTimeZones();

  datetimeTimezoneSelect.innerHTML = timeZones
    .map((timeZone) => `<option value="${timeZone}">${timeZone}</option>`)
    .join("");
  datetimeTimezoneSelect.value = timeZones.includes(timestampToolState.datetimeTimeZone)
    ? timestampToolState.datetimeTimeZone
    : getResolvedTimeZone();
}

function renderCurrentTimestampCard(timezone: string): void {
  const current = createCurrentTimestampResult();

  timestampLiveSecondsValue.textContent = current.seconds;
  timestampLiveMillisecondsValue.textContent = current.milliseconds;
  timestampLiveLocalValue.textContent = formatLocalDateTime(
    current.date,
    state.locale
  );
  timestampLiveUtcValue.textContent = current.isoUtc;
  timestampLiveTimezoneValue.textContent = timezone;
  timestampLiveSecondsCopy.dataset.copyText = current.seconds;
  timestampLiveMillisecondsCopy.dataset.copyText = current.milliseconds;
  timestampLiveLocalCopy.dataset.copyText = formatLocalDateTime(
    current.date,
    state.locale
  );
  timestampLiveUtcCopy.dataset.copyText = current.isoUtc;
  timestampLiveTimezoneCopy.dataset.copyText = timezone;
}

function renderTimestampToDateSection(pack: (typeof localePacks)[Locale]): void {
  const value = timestampToolState.timestampInput.trim();

  if (value === "") {
    timestampConvertError.hidden = true;
    timestampConvertError.textContent = "";
    timestampConvertResults.replaceChildren(
      createTimestampToolEmptyState(pack.timestampTool.emptyHint)
    );
    return;
  }

  try {
    const result = convertTimestampInput(value, timestampToolState.timestampUnit);

    timestampConvertError.hidden = true;
    timestampConvertError.textContent = "";
    timestampConvertResults.replaceChildren(
      ...[
        createTimestampItem(
          pack.timestampResult.localTime,
          formatLocalDateTime(result.date, state.locale)
        ),
        createTimestampItem(pack.timestampResult.isoUtc, result.isoUtc)
      ]
    );
  } catch {
    timestampConvertError.hidden = false;
    timestampConvertError.textContent = pack.errorMessages.INVALID_TIMESTAMP;
    timestampConvertResults.replaceChildren(
      createTimestampToolEmptyState(pack.timestampTool.emptyHint)
    );
  }
}

function renderDateTimeToTimestampSection(pack: (typeof localePacks)[Locale]): void {
  const value = timestampToolState.datetimeInput.trim();

  if (value === "") {
    datetimeConvertError.hidden = true;
    datetimeConvertError.textContent = "";
    datetimeConvertResults.replaceChildren(
      createTimestampToolEmptyState(pack.timestampTool.emptyHint)
    );
    return;
  }

  try {
    const result = convertDateTimeInput(
      value,
      timestampToolState.datetimeTimeZone
    );

    datetimeConvertError.hidden = true;
    datetimeConvertError.textContent = "";
    datetimeConvertResults.replaceChildren(
      ...[
        createTimestampItem(pack.timestampResult.seconds, result.seconds),
        createTimestampItem(
          pack.timestampResult.milliseconds,
          result.milliseconds
        )
      ]
    );
  } catch {
    datetimeConvertError.hidden = false;
    datetimeConvertError.textContent = pack.errorMessages.INVALID_TIMESTAMP;
    datetimeConvertResults.replaceChildren(
      createTimestampToolEmptyState(pack.timestampTool.emptyHint)
    );
  }
}

function renderCronTool(): void {
  const pack = localePacks[state.locale];
  const copy = pack.cronTool;
  const flavorCopy = copy.flavors[cronToolState.flavor];
  const expression = cronToolState.drafts[cronToolState.flavor];

  cronToolTitle.textContent = copy.title;
  cronToolSummary.textContent = copy.summary;
  cronCurrentTimezone.textContent = `${copy.currentTimezoneLabel} · ${
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  }`;
  cronTypeLabel.textContent = copy.typeLabel;
  cronExpressionLabel.textContent = copy.expressionLabel;
  cronExpressionInput.placeholder =
    pack.modeInputPlaceholders["cron-describe"] ?? pack.toolInputPlaceholders.cron;
  cronExpressionInput.value = expression;
  cronFieldGuideLabel.textContent = copy.fieldGuideLabel;
  cronFieldGuideValue.textContent = flavorCopy.fieldGuide;
  cronExampleLabel.textContent = copy.exampleLabel;
  cronExampleValue.textContent = flavorCopy.example;
  cronFlavorHint.textContent = flavorCopy.hint;
  cronDescriptionTitle.textContent = copy.descriptionLabel;
  cronNextRunsTitle.textContent = copy.nextRunsLabel;

  renderCronFlavorSwitch();

  const normalizedExpression = expression.trim();

  if (normalizedExpression === "") {
    cronToolError.hidden = true;
    cronToolError.textContent = "";
    cronDescriptionBody.replaceChildren(createCronEmptyState(copy.emptyHint));
    cronNextRunsList.replaceChildren(createCronEmptyState(copy.emptyHint));
    return;
  }

  try {
    const result = analyzeCronExpression(
      normalizedExpression,
      cronToolState.flavor,
      state.locale
    );

    cronToolError.hidden = true;
    cronToolError.textContent = "";
    cronDescriptionBody.replaceChildren(
      createCronDescriptionBlock(result.normalizedExpression, result.description)
    );
    cronNextRunsList.replaceChildren(
      ...result.nextRuns.map((run, index) => createCronRunItem(index + 1, run))
    );
  } catch {
    cronToolError.hidden = false;
    cronToolError.textContent = pack.errorMessages.INVALID_CRON_EXPRESSION;
    cronDescriptionBody.replaceChildren(createCronEmptyState(copy.emptyHint));
    cronNextRunsList.replaceChildren(createCronEmptyState(copy.emptyHint));
  }
}

function renderCronFlavorSwitch(): void {
  const flavors: CronFlavor[] = ["linux", "spring", "quartz"];
  const copy = localePacks[state.locale].cronTool.flavors;

  cronFlavorSwitch.innerHTML = flavors
    .map(
      (flavor) => `
        <button
          class="cron-flavor-button ${flavor === cronToolState.flavor ? "is-active" : ""}"
          data-cron-flavor="${flavor}"
          type="button"
        >
          <span>${copy[flavor].title}</span>
        </button>
      `
    )
    .join("");
}

function createCronDescriptionBlock(
  expression: string,
  description: string
): HTMLElement {
  const container = document.createElement("div");
  container.className = "cron-description-stack";

  const expressionRow = document.createElement("div");
  expressionRow.className = "cron-description-expression";

  const expressionCode = document.createElement("code");
  expressionCode.className = "cron-expression-code";
  expressionCode.textContent = expression;

  const copyButton = document.createElement("button");
  copyButton.className = "timestamp-copy-button";
  copyButton.type = "button";
  copyButton.dataset.copyText = expression;
  syncTimestampCopyButtonLabel(copyButton);

  expressionRow.append(expressionCode, copyButton);

  const descriptionText = document.createElement("p");
  descriptionText.className = "cron-description-text";
  descriptionText.textContent = description;

  container.append(expressionRow, descriptionText);
  return container;
}

function createCronRunItem(index: number, value: string): HTMLElement {
  const item = document.createElement("div");
  item.className = "cron-run-item";

  const indexBadge = document.createElement("span");
  indexBadge.className = "cron-run-index";
  indexBadge.textContent = String(index).padStart(2, "0");

  const valueText = document.createElement("code");
  valueText.className = "cron-run-value";
  valueText.textContent = value;

  const copyButton = document.createElement("button");
  copyButton.className = "timestamp-copy-button";
  copyButton.type = "button";
  copyButton.dataset.copyText = value;
  syncTimestampCopyButtonLabel(copyButton);

  item.append(indexBadge, valueText, copyButton);
  return item;
}

function createCronEmptyState(message: string): HTMLElement {
  const empty = document.createElement("div");
  empty.className = "cron-empty-state";
  empty.textContent = message;
  return empty;
}

function createTimestampToolEmptyState(message: string): HTMLElement {
  const empty = document.createElement("div");
  empty.className = "timestamp-tool-empty";
  empty.textContent = message;
  return empty;
}

function syncStaticTimestampCopyButtons(): void {
  for (const button of [
    timestampLiveSecondsCopy,
    timestampLiveMillisecondsCopy,
    timestampLiveLocalCopy,
    timestampLiveUtcCopy,
    timestampLiveTimezoneCopy
  ]) {
    syncTimestampCopyButtonLabel(button);
  }
}

function syncTimestampLiveTimer(): void {
  if (getActiveToolLayoutKind() !== "timestamp") {
    if (timestampLiveTimer !== null) {
      window.clearInterval(timestampLiveTimer);
      timestampLiveTimer = null;
    }

    return;
  }

  if (timestampLiveTimer !== null) {
    return;
  }

  timestampLiveTimer = window.setInterval(() => {
    renderCurrentTimestampCard(getResolvedTimeZone());
  }, 250);
}

function scheduleAutoRun(delay = AUTO_RUN_DELAY_MS): void {
  if (
    getActiveToolLayoutKind() === "timestamp" ||
    getActiveToolLayoutKind() === "cron"
  ) {
    return;
  }

  clearAutoRunTimer();

  if (getWorkerInput().trim() === "") {
    return;
  }

  autoRunTimer = window.setTimeout(() => {
    autoRunTimer = null;
    runActiveMode();
  }, delay);
}

function clearAutoRunTimer(): void {
  if (autoRunTimer !== null) {
    window.clearTimeout(autoRunTimer);
    autoRunTimer = null;
  }
}

function invalidatePendingResponse(): void {
  requestSequence += 1;
  state.busy = false;
}

function showActionFeedback(
  kind: "clear" | "copy" | "copy-empty" | "copy-error"
): void {
  const pack = localePacks[state.locale];

  if (kind === "clear") {
    setFeedback(clearInputFeedback, pack.actionFeedback.cleared, "clear");
    return;
  }

  if (kind === "copy") {
    setFeedback(copyOutputFeedback, pack.actionFeedback.copied, "copy");
    return;
  }

  if (kind === "copy-empty") {
    setFeedback(copyOutputFeedback, pack.actionFeedback.nothingToCopy, "copy");
    return;
  }

  setFeedback(copyOutputFeedback, pack.actionFeedback.copyFailed, "copy-error");
}

function setFeedback(
  element: HTMLSpanElement,
  message: string,
  group: "clear" | "copy" | "copy-error"
): void {
  element.textContent = message;
  element.hidden = false;

  if (group === "clear") {
    if (clearFeedbackTimer !== null) {
      window.clearTimeout(clearFeedbackTimer);
    }

    clearFeedbackTimer = window.setTimeout(() => {
      element.hidden = true;
      clearFeedbackTimer = null;
    }, 1400);

    return;
  }

  if (copyFeedbackTimer !== null) {
    window.clearTimeout(copyFeedbackTimer);
  }

  copyFeedbackTimer = window.setTimeout(() => {
    element.hidden = true;
    copyFeedbackTimer = null;
  }, group === "copy" ? 1400 : 1800);
}

function getTool(toolId: ToolId): ToolDefinition {
  const tool = toolLookup.get(toolId);

  if (!tool) {
    throw new Error(`Unknown tool: ${toolId}`);
  }

  return tool;
}

function getActiveToolLayoutKind(): ToolLayoutKind {
  return getTool(state.activeToolId).layoutKind;
}

function getUrlActionId(modeId: ToolModeId): UrlActionId {
  return modeId === "url-decode" ? "decode" : "encode";
}

function getWorkerInput(): string {
  if (state.activeModeId !== "text-diff-compare") {
    return state.input;
  }

  return JSON.stringify({
    left: state.input,
    right: state.secondaryInput
  });
}

function handleUrlActionChange(nextAction: UrlActionId): void {
  if (state.activeToolId !== "url-encode") {
    return;
  }

  if (nextAction === "decode") {
    setActiveMode("url-decode");
    return;
  }

  setActiveMode(lastUrlEncodeModeId);
}

function setActiveMode(nextModeId: ToolModeId): void {
  if (nextModeId === state.activeModeId) {
    return;
  }

  invalidatePendingResponse();
  state.input = switchModeInputDraft(
    modeInputDrafts,
    state.activeModeId,
    state.input,
    nextModeId
  );
  state.secondaryInput = switchModeInputDraft(
    secondaryModeInputDrafts,
    state.activeModeId,
    state.secondaryInput,
    nextModeId
  );

  if (isUrlEncodeModeId(nextModeId)) {
    lastUrlEncodeModeId = nextModeId;
  }

  state.activeModeId = nextModeId;
  state.output = "";
  state.stats = null;
  state.errorCode = null;
  state.statusKind = "idle";
  inputEditor.value = state.input;
  outputEditor.value = state.secondaryInput;
  window.localStorage.setItem(storageKeys.mode, nextModeId);
  syncStartupQueryParams();
  render();
  scheduleAutoRun(0);
}

function renderModeButtons(modeIds: ToolModeId[], variantClass = ""): string {
  const pack = localePacks[state.locale];

  return modeIds
    .map((modeId) => {
      const modeCopy = pack.modes[modeId];

      return `
        <button
          class="mode-button ${variantClass} ${modeId === state.activeModeId ? "is-active" : ""}"
          data-mode-id="${modeId}"
          title="${modeCopy.summary}"
          type="button"
        >
          <span>${modeCopy.title}</span>
        </button>
      `;
    })
    .join("");
}

function getJsonTreeValue(): unknown | null {
  if (jsonTreeCacheOutput === state.output) {
    return jsonTreeCacheValue;
  }

  try {
    jsonTreeCacheValue = JSON.parse(state.output) as unknown;
    jsonTreeCacheOutput = state.output;
    return jsonTreeCacheValue;
  } catch {
    jsonTreeCacheValue = null;
    jsonTreeCacheOutput = state.output;
    return null;
  }
}

function parseJsonOutput<ValueType>(): ValueType | null {
  try {
    return JSON.parse(state.output) as ValueType;
  } catch {
    return null;
  }
}

function createTimestampItem(label: string, value: string): HTMLElement {
  const item = document.createElement("div");
  item.className = "timestamp-result-item";

  const head = document.createElement("div");
  head.className = "timestamp-item-head";

  const title = document.createElement("div");
  title.className = "timestamp-result-label";
  title.textContent = label;

  const copyButton = document.createElement("button");
  copyButton.className = "timestamp-copy-button";
  copyButton.type = "button";
  copyButton.dataset.copyText = value;
  syncTimestampCopyButtonLabel(copyButton);

  const content = document.createElement("div");
  content.className = "timestamp-result-value";
  content.textContent = value;

  head.append(title, copyButton);
  item.append(head, content);
  return item;
}

function syncTimestampCopyButtonLabel(button: HTMLButtonElement): void {
  button.textContent = getTimestampCopyDefaultLabel();
  button.classList.remove("is-copied", "is-failed");
  button.title = button.textContent;
}

function flashTimestampCopyButton(
  button: HTMLButtonElement,
  copied: boolean
): void {
  const activeTimer = timestampCopyTimers.get(button);

  if (activeTimer !== undefined) {
    window.clearTimeout(activeTimer);
  }

  button.textContent = copied
    ? state.locale === "zh"
      ? "已复制"
      : "Copied"
    : state.locale === "zh"
      ? "失败"
      : "Failed";
  button.title = button.textContent;
  button.classList.toggle("is-copied", copied);
  button.classList.toggle("is-failed", !copied);

  const timer = window.setTimeout(() => {
    syncTimestampCopyButtonLabel(button);
    timestampCopyTimers.delete(button);
  }, copied ? 1200 : 1600);

  timestampCopyTimers.set(button, timer);
}

function getTimestampCopyDefaultLabel(): string {
  return localePacks[state.locale].copyLabel;
}

function createTextDiffEmptyState(): HTMLElement {
  const empty = document.createElement("div");
  empty.className = "text-diff-empty";
  empty.textContent = localePacks[state.locale].textDiff.empty;
  return empty;
}

function createTextDiffRow(row: {
  type: "unchanged" | "added" | "removed";
  leftLine: number | null;
  rightLine: number | null;
  text: string;
}): HTMLElement {
  const element = document.createElement("div");
  element.className = `text-diff-row text-diff-row--${row.type}`;

  const leftLine = document.createElement("span");
  leftLine.className = "text-diff-line";
  leftLine.textContent = row.leftLine === null ? "" : String(row.leftLine);

  const rightLine = document.createElement("span");
  rightLine.className = "text-diff-line";
  rightLine.textContent = row.rightLine === null ? "" : String(row.rightLine);

  const marker = document.createElement("span");
  marker.className = "text-diff-marker";
  marker.textContent = row.type === "added" ? "+" : row.type === "removed" ? "-" : " ";

  const text = document.createElement("pre");
  text.className = "text-diff-text";
  text.textContent = row.text;

  element.append(leftLine, rightLine, marker, text);
  return element;
}

function createJsonTreeRoot(value: unknown): HTMLElement {
  const root = document.createElement("div");
  root.className = "json-tree-root";

  if (Array.isArray(value) || isPlainObject(value)) {
    root.appendChild(createJsonContainerNode(null, value, 0));
    return root;
  }

  root.appendChild(createJsonLeaf(null, value));
  return root;
}

type JsonTreeKey = string | number | null;

function populateJsonChildren(
  container: HTMLElement,
  value: unknown[] | Record<string, unknown>,
  depth: number
): void {
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      container.appendChild(createJsonNode(index, item, depth));
    });
    return;
  }

  Object.entries(value).forEach(([key, item]) => {
    container.appendChild(createJsonNode(key, item, depth));
  });
}

function createJsonNode(
  key: JsonTreeKey,
  value: unknown,
  depth: number
): HTMLElement {
  if (Array.isArray(value)) {
    return createJsonContainerNode(key, value, depth);
  }

  if (isPlainObject(value)) {
    return createJsonContainerNode(key, value, depth);
  }

  return createJsonLeaf(key, value);
}

function createJsonContainerNode(
  key: JsonTreeKey,
  value: unknown[] | Record<string, unknown>,
  depth: number
): HTMLElement {
  const pack = localePacks[state.locale];
  const count = Array.isArray(value) ? value.length : Object.keys(value).length;

  if (count === 0) {
    return createJsonStaticLeaf(
      key,
      Array.isArray(value) ? pack.jsonTree.emptyArray : pack.jsonTree.emptyObject
    );
  }

  const details = document.createElement("details");
  details.className = "json-tree-node";
  details.open = true;

  const summary = document.createElement("summary");
  summary.className = "json-tree-summary";
  const keyLabel = createJsonKey(key);

  if (keyLabel !== null) {
    summary.appendChild(keyLabel);
  }

  summary.appendChild(createJsonContainerToken(value));

  const meta = document.createElement("span");
  meta.className = "json-tree-meta";
  meta.textContent = Array.isArray(value)
    ? pack.jsonTree.arrayCount(count)
    : pack.jsonTree.objectCount(count);
  summary.appendChild(meta);

  const children = document.createElement("div");
  children.className = "json-tree-children";

  const closing = document.createElement("div");
  closing.className = "json-tree-closing";
  closing.textContent = Array.isArray(value) ? "]" : "}";

  details.append(summary, children, closing);

  if (details.open) {
    populateJsonChildren(children, value, depth + 1);
  }

  details.addEventListener("toggle", () => {
    if (!details.open) {
      return;
    }

    if (children.childElementCount === 0) {
      populateJsonChildren(children, value, depth + 1);
    }
  });

  return details;
}

function createJsonLeaf(key: JsonTreeKey, value: unknown): HTMLElement {
  const row = document.createElement("div");
  row.className = "json-tree-leaf";
  const keyLabel = createJsonKey(key);

  if (keyLabel !== null) {
    row.appendChild(keyLabel);
  }

  const valueLabel = document.createElement("span");
  valueLabel.className = `json-tree-value ${getJsonValueClassName(value)}`;
  valueLabel.textContent =
    typeof value === "string" ? JSON.stringify(value) : String(value);
  row.appendChild(valueLabel);

  return row;
}

function createJsonStaticLeaf(key: JsonTreeKey, value: string): HTMLElement {
  const row = document.createElement("div");
  row.className = "json-tree-leaf";
  const keyLabel = createJsonKey(key);

  if (keyLabel !== null) {
    row.appendChild(keyLabel);
  }

  const valueLabel = document.createElement("span");
  valueLabel.className = "json-tree-value json-tree-value--plain";
  valueLabel.textContent = value;
  row.appendChild(valueLabel);

  return row;
}

function createJsonKey(key: JsonTreeKey): HTMLElement | null {
  if (key === null) {
    return null;
  }

  const keyLabel = document.createElement("span");
  keyLabel.className = "json-tree-key";
  keyLabel.textContent =
    typeof key === "number" ? `[${key}]: ` : `${JSON.stringify(key)}: `;
  return keyLabel;
}

function createJsonContainerToken(
  value: unknown[] | Record<string, unknown>
): HTMLElement {
  const token = document.createElement("span");
  token.className = "json-tree-token";
  token.textContent = Array.isArray(value) ? "[" : "{";
  return token;
}

function getJsonValueClassName(value: unknown): string {
  if (typeof value === "string") {
    return "json-tree-value--string";
  }

  if (typeof value === "number") {
    return "json-tree-value--number";
  }

  if (typeof value === "boolean") {
    return "json-tree-value--boolean";
  }

  if (value === null) {
    return "json-tree-value--null";
  }

  return "json-tree-value--plain";
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getElement<ElementType extends Element>(selector: string): ElementType {
  const element = document.querySelector<ElementType>(selector);

  if (!element) {
    throw new Error(`Missing element: ${selector}`);
  }

  return element;
}

function syncStartupQueryParams(): void {
  const url = new URL(window.location.href);
  url.searchParams.set("tool", state.activeToolId);
  url.searchParams.set("mode", state.activeModeId);
  url.searchParams.set("lang", state.locale);

  window.history.replaceState({}, "", url);
}

function resetToHome(): void {
  const homeTool = getTool(defaultToolId);

  clearAutoRunTimer();
  invalidatePendingResponse();
  state.activeToolId = homeTool.id;
  state.activeModeId = homeTool.defaultModeId;
  state.input = "";
  state.secondaryInput = "";
  state.output = "";
  state.busy = false;
  state.stats = null;
  state.statusKind = "idle";
  state.errorCode = null;

  for (const modeId of Object.keys(modeInputDrafts) as ToolModeId[]) {
    modeInputDrafts[modeId] = "";
    secondaryModeInputDrafts[modeId] = "";
  }

  inputEditor.value = "";
  outputEditor.value = "";
  window.localStorage.removeItem(storageKeys.tool);
  window.localStorage.removeItem(storageKeys.mode);
  syncHomeQueryParams();
  render();
  inputEditor.focus();
}

function syncHomeQueryParams(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete("tool");
  url.searchParams.delete("mode");

  if (state.locale === "zh") {
    url.searchParams.delete("lang");
  } else {
    url.searchParams.set("lang", state.locale);
  }

  window.history.replaceState({}, "", url);
}
